"use client";

import { useEffect, useRef, useState } from "react";
import { useMotionValue } from "framer-motion";
import type {
  UseCarouselScrollStateParams,
  UseCarouselScrollStateResult,
} from "./carouselScrollStateTypes";
import { useCarouselCallbackRefs } from "./useCarouselCallbackRefs";
import { useCarouselImperativeScroll } from "./useCarouselImperativeScroll";
import { useCarouselInitialPosition } from "./useCarouselInitialPosition";
import { useCarouselScrollSettling } from "./useCarouselScrollSettling";
import { useCarouselSnapReporter } from "./useCarouselSnapReporter";

export function useCarouselScrollState({
  initialDoubledIndex,
  middleCopyEnd,
  middleCopyStart,
  onCenteredTeamChange,
  onTeamSelect,
  originalLength,
  pitch,
  ref,
  teams,
}: UseCarouselScrollStateParams): UseCarouselScrollStateResult {
  const scrollX = useMotionValue(initialDoubledIndex * pitch);
  const containerRef = useRef<HTMLDivElement>(null);
  const scrollSettledTimerRef = useRef<number | null>(null);
  const [centeredDoubledIndex, setCenteredDoubledIndex] =
    useState(initialDoubledIndex);
  const { handleSelectTeam, onCenteredTeamChangeRef } =
    useCarouselCallbackRefs({ onCenteredTeamChange, onTeamSelect });
  const { lastReportedTeamIdRef, reportSnap } = useCarouselSnapReporter({
    onCenteredTeamChangeRef,
    originalLength,
    teams,
  });
  const handleScrollToIndex = useCarouselImperativeScroll({
    containerRef,
    pitch,
    ref,
  });

  useCarouselScrollSettling({
    containerRef,
    middleCopyEnd,
    middleCopyStart,
    originalLength,
    pitch,
    reportSnap,
    scrollSettledTimerRef,
    scrollX,
    setCenteredDoubledIndex,
  });
  useCarouselInitialPosition({
    containerRef,
    initialDoubledIndex,
    lastReportedTeamIdRef,
    originalLength,
    pitch,
    reportSnap,
    scrollX,
    setCenteredDoubledIndex,
  });
  useEffect(
    () => () => {
      if (scrollSettledTimerRef.current != null) {
        window.clearTimeout(scrollSettledTimerRef.current);
      }
    },
    [],
  );

  return {
    centeredDoubledIndex,
    containerRef,
    handleScrollToIndex,
    handleSelectTeam,
    scrollX,
  };
}
